import { Fragment, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { EvidenceGallery } from '@/components/EvidenceGallery';
import { SkuMeta } from '@/components/SkuMeta';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { ActionError, newOpId, plainQty } from '@/pages/Exceptions/shared';
import { STOCK_REASON_CODES, type InboundActivity, type InboundLine, type InboundReceipt } from '@/api/types';
import { NextStepCard } from './NextStep';
import { RequestRevisionNotice } from '@/components/RequestRevisionNotice';
import { ReceiveLinePanel } from './ReceiveLinePanel';
import { PutawayCard } from './PutawayCard';
import { ScanReceivePanel } from './ScanReceivePanel';
import {
  PARTIES, describeReceivingError, discrepancyLabel, notFullyReceived, partyLabel, undecidedCount, type PartyCode, type StepAction,
} from './receivingModel';
import type { TranslateFn } from '@/i18n/LanguageContext';

/**
 * An inbound document from the warehouse's side: what to do next (here and on the handheld), the
 * lines with expected/received progress, receiving by hand, who received what and when, and the
 * return dispositions if any. Receiving itself is the existing engine (office routes call the same
 * services as the handheld); nothing here is a second implementation.
 *
 * REGULATED EVIDENCE epic (ADR-074): each line has a real `EvidenceGallery`
 * (`entityKind: 'INBOUND_LINE'`) - the discrepancy photo captured at receiving.
 */
export function InboundDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.inbound(id!), [id]);
  const { hasPermission } = useAuth();
  const canReadEvidence = hasPermission('EVIDENCE.READ');
  const canWrite = hasPermission('RECEIVING.WRITE');
  const canResolveOverage = hasPermission('RECEIVING.RESOLVE_OVERAGE');
  const canPutaway = hasPermission('PUTAWAY.WRITE');
  const [expandedLine, setExpandedLine] = useState<string | null>(null);
  const [receivingLine, setReceivingLine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stepOps = useRef<Partial<Record<StepAction, string>>>({});
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { inbound: header, lines, dispositions, receiving_locations: locations, receipts, activity } = result.data;
  const ownerId = header.owner_id;
  const number = header.number;

  async function runStep(action: StepAction): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    const opId = (stepOps.current[action] ??= newOpId());
    try {
      try {
        await endpoints.inboundStep(id!, action, opId);
      } catch (err) {
        // A shortage is finished only on an explicit confirmation; the refused call wrote nothing, so the same opId is reused.
        if (action === 'FINISH' && err instanceof ApiError && err.code === 'RECEIVING.SHORTAGE_NEEDS_CONFIRMATION' && window.confirm(describeReceivingError(t, err))) {
          await endpoints.inboundStep(id!, action, opId, true);
        } else {
          throw err;
        }
      }
      delete stepOps.current[action];
      setMessage(t(`rcv.done.${action}` as const));
      result.reload();
    } catch (err) {
      setError(describeReceivingError(t, err));
    } finally {
      setBusy(false);
    }
  }

  const lastReceipt = (lineId: string): InboundReceipt | undefined => [...receipts].reverse().find((r) => r.inbound_line_id === lineId);
  const canReceive = canWrite && header.state === 'RECEIVING' && header.kind !== 'RETURN';

  return (
    <div>
      <div className="page-header">
        <h1>{t('inboundDetail.title', { number })}</h1>
        <StatusBadge value={header.state} />
        {Number(header.awaiting_putaway ?? 0) > 0 && (
          <span className="badge warn" title={t('putaway.awaitingHint')}>
            {t('putaway.awaitingBadge', { n: formatNumber(header.awaiting_putaway!, locale) })}
          </span>
        )}
      </div>

      {message && (
        <div className="card success-card" role="status">
          {message}
        </div>
      )}
      {error && <ActionError message={error} />}

      <RequestRevisionNotice kind="INBOUND" revisionNo={header.revision_no} revisions={result.data.revisions} />

      <NextStepCard
        number={number}
        receivingCodes={locations.filter((l) => l.kind === 'RECEIVING').map((l) => l.code)}
        busy={busy}
        onAction={(a) => void runStep(a)}
        input={{
          state: header.state,
          kind: header.kind,
          canWrite,
          hasReceivingZone: locations.some((l) => l.kind === 'RECEIVING'),
          undecidedCount: undecidedCount(lines),
          notFullyReceived: notFullyReceived(lines),
        }}
      />

      {canReceive && lines.length > 0 && (
        <ScanReceivePanel
          inboundId={id!}
          number={number}
          ownerId={ownerId}
          ownerName={header.owner_name}
          warehouseId={header.warehouse_id}
          warehouseName={header.warehouse_name}
          receivingCode={locations.find((l) => l.kind === 'RECEIVING')?.code ?? null}
          lines={lines}
          canPutaway={canPutaway}
          onChanged={result.refresh}
        />
      )}

      {header.kind !== 'RETURN' && (
        <PutawayCard
          inboundId={id!}
          warehouseName={header.warehouse_name}
          state={header.state}
          canPutaway={canPutaway}
          refreshKey={`${header.state}:${receipts.length}`}
          onChanged={result.reload}
        />
      )}

      <div className="card">
        <dl className="kv">
          <dt>{t('inbounds.colSeller')}</dt>
          <dd>{header.owner_name}</dd>
          <dt>{t('inbounds.colWarehouse')}</dt>
          <dd>
            {header.warehouse_name}
            {header.warehouse_code && <> <span className="badge neutral">{header.warehouse_code}</span></>}
          </dd>
          <dt>{t('inboundDetail.kind')}</dt>
          <dd>{statusLabel(t, header.kind)}</dd>
          <dt>{t('inbounds.colExpected')}</dt>
          <dd>{header.expected_at ? formatDateTime(header.expected_at, locale) : '—'}</dd>
          <dt>{t('inboundDetail.arrived')}</dt>
          <dd>{header.arrived_at ? formatDateTime(header.arrived_at, locale) : '—'}</dd>
          {header.requested_by_seller === true && (
            <>
              <dt>{t('inboundDetail.source')}</dt>
              <dd>{t('inboundDetail.sourceSellerPortal')}</dd>
            </>
          )}
        </dl>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inboundDetail.lines')}</h2>
        {lines.length === 0 ? (
          <p>{t('inboundDetail.noLines')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('rcv.colProduct')}</th>
                <th>{t('rcv.colProgress')}</th>
                <th>{t('inboundDetail.discrepancy')}</th>
                <th>{t('inboundDetail.responsibility')}</th>
                <th>{t('rcv.colReceivedBy')}</th>
                <th>{t('inboundDetail.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const last = lastReceipt(line.id);
                const missing = Number(line.qty_expected) - Number(line.qty_received);
                return (
                  <Fragment key={line.id}>
                    <tr data-testid="inbound-line">
                      <td>
                        {line.sku_code ? (
                          <>
                            <div>{line.sku_code} — {line.sku_name}</div>
                            <SkuMeta code={line.sku_code} article={line.article} size={line.size} color={line.color} barcodes={line.barcodes} />
                          </>
                        ) : (
                          <div>{t('rcv.unidentified')}</div>
                        )}
                        {canReceive && (
                          <button type="button" className="primary" onClick={() => setReceivingLine(receivingLine === line.id ? null : line.id)}>
                            {t('rcv.receive')}
                          </button>
                        )}
                      </td>
                      <td>
                        <div>{t('rcv.progress', { received: formatNumber(line.qty_received, locale), expected: formatNumber(line.qty_expected, locale) })}</div>
                        {missing > 0 && header.state !== 'EXPECTED' && header.state !== 'ARRIVED' && (
                          <div className="meta">{t('rcv.remaining', { n: plainQty(String(missing)) })}</div>
                        )}
                        {missing < 0 && <div className="meta">{t('rcv.over', { n: plainQty(String(-missing)) })}</div>}
                        {missing < 0 && (
                          <OverageCell
                            inboundId={id!}
                            line={line}
                            canDecide={canResolveOverage && header.state !== 'CANCELLED'}
                            t={t}
                            onSaved={(m) => { setMessage(m); setError(null); result.reload(); }}
                            onError={(m) => setError(m)}
                          />
                        )}
                      </td>
                      <td>
                        {line.discrepancy === 'NONE' ? <StatusBadge value="NONE" /> : <span className="badge danger">{discrepancyLabel(t, line.discrepancy)}</span>}
                        {line.discrepancy_note ? <div className="sub">{line.discrepancy_note}</div> : null}
                      </td>
                      <td>
                        {line.discrepancy !== 'NONE' && canWrite && header.state !== 'CLOSED' && header.state !== 'CANCELLED' ? (
                          <ResponsibilityCell
                            inboundId={id!}
                            line={line}
                            t={t}
                            onSaved={(m) => { setMessage(m); setError(null); result.reload(); }}
                            onError={(m) => setError(m)}
                          />
                        ) : line.discrepancy === 'NONE' ? (
                          '—'
                        ) : (
                          partyLabel(t, line.responsible_party)
                        )}
                      </td>
                      <td>
                        {last ? (
                          <>
                            <div>{last.by_device ? t('rcv.history.byDevice', { name: last.actor_name ?? '' }) : last.actor_name ?? t('rcv.history.staff')}</div>
                            <div className="meta">{formatDateTime(last.occurred_at, locale)}</div>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <button type="button" onClick={() => setExpandedLine(expandedLine === line.id ? null : line.id)}>
                          {expandedLine === line.id ? t('common.hide') : t('inboundDetail.photos')}
                        </button>
                      </td>
                    </tr>
                    {receivingLine === line.id && canReceive && (
                      <tr>
                        <td colSpan={6}>
                          <ReceiveLinePanel
                            inboundId={id!}
                            ownerId={ownerId}
                            line={line}
                            locations={locations}
                            warehouseName={header.warehouse_name}
                            onClose={() => setReceivingLine(null)}
                            onDone={(m) => { setReceivingLine(null); setMessage(m); setError(null); result.reload(); }}
                          />
                        </td>
                      </tr>
                    )}
                    {expandedLine === line.id && (
                      <tr>
                        <td colSpan={6}>
                          <EvidenceGallery
                            entityKind="INBOUND_LINE"
                            entityId={line.id}
                            ownerId={ownerId}
                            skuId={line.sku_id}
                            canRead={canReadEvidence}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('rcv.history')}</h2>
        {receipts.length === 0 ? (
          <p>{t('rcv.history.empty')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('rcv.colTime')}</th>
                <th>{t('rcv.colProduct')}</th>
                <th>{t('inboundDetail.colQty')}</th>
                <th>{t('rcv.location')}</th>
                <th>{t('rcv.colWho')}</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((r) => {
                const line = lines.find((l) => l.id === r.inbound_line_id);
                return (
                  <tr key={r.id}>
                    <td>{formatDateTime(r.occurred_at, locale)}</td>
                    <td>{line?.sku_code ? `${line.sku_code} — ${line.sku_name}` : '—'}</td>
                    <td>{formatNumber(r.qty, locale)}</td>
                    <td>{r.location_code}</td>
                    <td>{r.by_device ? t('rcv.history.byDevice', { name: r.actor_name ?? '' }) : r.actor_name ?? t('rcv.history.staff')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
        {activity.length > 0 && (
          <>
            <h3 className="card-title" style={{ marginTop: 16 }}>{t('rcv.activity')}</h3>
            <ul className="activity-list">
              {activity.map((a) => (
                <li key={a.id}>
                  <span className="meta">{formatDateTime(a.occurred_at, locale)}</span>{' '}
                  {activityText(t, a)}
                  <span className="meta"> · {a.actor_name ?? t('rcv.history.staff')}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('inboundDetail.dispositionHistory')}</h2>
        {dispositions.length === 0 ? (
          <p>{t('inboundDetail.noDispositions')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inboundDetail.colQty')}</th>
                <th>{t('inboundDetail.colDisposition')}</th>
                <th>{t('inboundDetail.colToLocation')}</th>
                <th>{t('inboundDetail.colRecorded')}</th>
                <th>{t('inboundDetail.colReversed')}</th>
              </tr>
            </thead>
            <tbody>
              {dispositions.map((row, i) => {
                const r = row as Record<string, unknown>;
                return (
                  <tr key={i}>
                    <td>{formatNumber(String(r.qty), locale)}</td>
                    <td>
                      <StatusBadge value={String(r.disposition)} />
                    </td>
                    <td>{r.to_location_code ? String(r.to_location_code) : '—'}</td>
                    <td>{formatDateTime(String(r.created_at), locale)}</td>
                    <td>{r.reversed_at ? formatDateTime(String(r.reversed_at), locale) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function activityText(t: TranslateFn, a: InboundActivity): string {
  const p = a.payload;
  switch (a.action) {
    case 'INBOUND_ARRIVED': return t('rcv.log.INBOUND_ARRIVED');
    case 'INBOUND_RECEIVING_STARTED': return t('rcv.log.INBOUND_RECEIVING_STARTED');
    case 'INBOUND_RECEIVING_FINISHED': return t('rcv.log.INBOUND_RECEIVING_FINISHED');
    case 'INBOUND_CLOSED': return t('rcv.log.INBOUND_CLOSED');
    case 'INBOUND_LINE_RECEIVED': return t('rcv.log.INBOUND_LINE_RECEIVED', { qty: plainQty(String(p.qty ?? '')) });
    case 'INBOUND_PUTAWAY': return t('rcv.log.INBOUND_PUTAWAY', { qty: plainQty(String(p.qty ?? '')), cell: String(p.toLocationCode ?? '') });
    case 'INBOUND_RESPONSIBILITY_ASSIGNED': return t('rcv.log.INBOUND_RESPONSIBILITY_ASSIGNED', { party: partyLabel(t, String(p.responsibleParty ?? '')) });
    default: return t('rcv.log.other');
  }
}

/** Who is responsible for a line's discrepancy - assignable until the receiving is closed (closing needs it decided). */
function ResponsibilityCell({
  inboundId, line, t, onSaved, onError,
}: {
  readonly inboundId: string;
  readonly line: InboundLine;
  readonly t: TranslateFn;
  readonly onSaved: (message: string) => void;
  readonly onError: (message: string) => void;
}): JSX.Element {
  const [value, setValue] = useState<PartyCode>(line.responsible_party as PartyCode);
  const [busy, setBusy] = useState(false);
  async function save(): Promise<void> {
    if (value === 'UNDECIDED') return;
    setBusy(true);
    try {
      await endpoints.inboundResponsibility(inboundId, { inboundLineId: line.id, responsibleParty: value });
      onSaved(t('rcv.assigned'));
    } catch (err) {
      onError(describeReceivingError(t, err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="action-row">
      <select aria-label={t('inboundDetail.responsibility')} value={value} onChange={(e) => setValue(e.target.value as PartyCode)}>
        {PARTIES.map((p) => (
          <option key={p} value={p} disabled={p === 'UNDECIDED' && line.responsible_party !== 'UNDECIDED'}>
            {partyLabel(t, p)}
          </option>
        ))}
      </select>
      <button type="button" disabled={busy || value === 'UNDECIDED' || value === line.responsible_party} onClick={() => void save()}>
        {t('rcv.assign')}
      </button>
    </div>
  );
}

/**
 * A surplus has its own decision, separate from the responsible party: PENDING (waiting, held out of putaway) ->
 * RESOLVED (accepted as stock) or REJECTED (stays held, needs separate handling) -> DISPOSED (handled). Only ADMIN
 * decides; everybody else sees the state. Nothing here changes a quantity or the stock.
 */
function OverageCell({
  inboundId, line, canDecide, t, onSaved, onError,
}: {
  readonly inboundId: string;
  readonly line: InboundLine;
  readonly canDecide: boolean;
  readonly t: TranslateFn;
  readonly onSaved: (message: string) => void;
  readonly onError: (message: string) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const state = line.overage_resolution ?? 'PENDING';
  async function decide(resolution: 'RESOLVED' | 'REJECTED'): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await endpoints.inboundOverage(inboundId, { opId: newOpId(), inboundLineId: line.id, resolution });
      onSaved(t('rcv.overage.saved'));
    } catch (err) {
      onError(describeReceivingError(t, err));
    } finally {
      setBusy(false);
    }
  }
  const held = state === 'PENDING' || state === 'REJECTED';
  return (
    <div className="sub" data-testid="overage-cell">
      <span className={held ? 'badge danger' : 'badge'}>{t(`rcv.overage.state.${state}` as const)}</span>
      {held ? <div className="meta">{t('rcv.overage.held')}{line.overage_held ? ` (${plainQty(line.overage_held)})` : ''}</div> : null}
      {canDecide && state === 'PENDING' ? (
        <div className="action-row">
          <button type="button" disabled={busy} onClick={() => { if (window.confirm(t('rcv.overage.confirmResolve'))) void decide('RESOLVED'); }}>{t('rcv.overage.resolve')}</button>
          <button type="button" disabled={busy} onClick={() => void decide('REJECTED')}>{t('rcv.overage.reject')}</button>
        </div>
      ) : null}
      {canDecide && state === 'REJECTED' ? (
        <>
          <div className="action-row">
            <button type="button" disabled={busy} onClick={() => { if (window.confirm(t('rcv.overage.confirmResolve'))) void decide('RESOLVED'); }}>{t('rcv.overage.resolve')}</button>
          </div>
          <DisposeForm inboundId={inboundId} line={line} t={t} onSaved={onSaved} onError={onError} />
        </>
      ) : null}
    </div>
  );
}

/** Confirmed withdrawal of a rejected surplus: how many, how (write-off / quarantine), why. The hold shrinks by exactly this. */
function DisposeForm({
  inboundId, line, t, onSaved, onError,
}: {
  readonly inboundId: string;
  readonly line: InboundLine;
  readonly t: TranslateFn;
  readonly onSaved: (message: string) => void;
  readonly onError: (message: string) => void;
}): JSX.Element {
  const held = String(line.overage_held ?? '');
  const [qty, setQty] = useState(held.replace(/\.?0+$/, ''));
  const [disposition, setDisposition] = useState<'WRITE_OFF' | 'QUARANTINE'>('WRITE_OFF');
  const [reason, setReason] = useState<string>('OTHER');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  async function save(): Promise<void> {
    if (busy || !window.confirm(t('rcv.overage.confirmDispose', { qty }))) return;
    setBusy(true);
    try {
      await endpoints.inboundOverageDispose(inboundId, { opId: newOpId(), inboundLineId: line.id, qty, disposition, reasonCode: reason, ...(note.trim() ? { note: note.trim() } : {}) });
      onSaved(t('rcv.overage.disposeSaved'));
    } catch (err) {
      onError(describeReceivingError(t, err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="action-row" data-testid="overage-dispose">
      <input aria-label={t('rcv.overage.disposeQty')} inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} size={5} />
      <select aria-label={t('rcv.overage.disposeHow')} value={disposition} onChange={(e) => setDisposition(e.target.value as 'WRITE_OFF' | 'QUARANTINE')}>
        <option value="WRITE_OFF">{t('rcv.overage.writeOff')}</option>
        <option value="QUARANTINE">{t('rcv.overage.quarantine')}</option>
      </select>
      <select aria-label={t('rcv.overage.disposeReason')} value={reason} onChange={(e) => setReason(e.target.value)}>
        {STOCK_REASON_CODES.map((r) => <option key={r} value={r}>{t(`reason.${r}` as const)}</option>)}
      </select>
      <input aria-label={t('rcv.overage.disposeNote')} placeholder={t('rcv.overage.disposeNote')} value={note} onChange={(e) => setNote(e.target.value)} />
      <button type="button" disabled={busy || !qty} onClick={() => void save()}>{t('rcv.overage.dispose')}</button>
    </div>
  );
}
